import React, { Component } from "react";
import { connect } from "react-redux";
import { withRouter } from "react-router-dom";

import HelperClass from "./../../../config/helperClass";
import {
  listClientsWithCredits,
  addClientCredit,
  deductClientCredit,
  getClientCreditLedger,
} from "./../../../config/api_calls";
import { addSettings } from "../../../actions";
import Select, { components as SelectComponents } from "react-select";
import DataTable from "../../../components/Tables/DataTable";
import PaginatedTable from "../../../components/Tables/PaginatedTable";
import PageTitle from "../ExtraComponents/PageTitle";
import MsaWarning from "../ExtraComponents/MsaWarning";
import SettingsError from "../ExtraComponents/SettingsError";
import { tableLoader } from "../../../components/ContentLoaders/table_loader";
import Tooltip from "@material-ui/core/Tooltip";
import MutextField from "@material-ui/core/TextField";
import MenuItem from "@material-ui/core/MenuItem";
import Autocomplete from "@material-ui/lab/Autocomplete";
import NotificationToast from "../../../components/Notification/NotificationToast";

const auth = new HelperClass();

// Strips the non-standard `isEmpty` prop react-select v5 forwards to native <input>
const ReactSelectInput = ({ isEmpty: _isEmpty, ...props }) => (
  <SelectComponents.Input {...props} />
);

class LoadCredit extends Component {
  constructor(props) {
    super(props);
    this.state = {
      // Toast notification
      toastMessage: "",
      toastType: "success",
      toastTitle: "",

      // Master client list (full reseller scope)
      allClients: [],
      tableData: [],
      totals: {
        total_balance: 0,
        total_loaded: 0,
        total_applied: 0,
        clients_with_credit: 0,
      },
      tableLoader: true,
      errorMessage: "",

      // Add Credit modal
      showAddModal: false,
      addClientAccountno: "",
      addAmount: "",
      addDate: new Date().toISOString().split("T")[0],
      addPaymentMethod: "Bank ACH",
      addReference: "",
      addMemo: "",
      addSubmitting: false,
      addError: "",
      addSuccess: "",

      // Deduct Credit modal
      showDeductModal: false,
      deductClientAccountno: "",
      deductAmount: "",
      deductDate: new Date().toISOString().split("T")[0],
      deductPaymentMethod: "Bank ACH",
      deductReference: "",
      deductMemo: "",
      deductSubmitting: false,
      deductError: "",
      deductSuccess: "",

      // Cached select options (computed once in fetchClients)
      clientSelectOptions: [],
      addClientOption: null,
      deductClientOption: null,

      // Ledger modal
      showLedgerModal: false,
      ledgerClient: null,
      ledgerRows: [],
      ledgerPage: 1,
      ledgerLimit: 10,
      ledgerTotal: 0,
      ledgerLoading: false,
      ledgerError: "",
    };
    // Cache decimal precision once — avoids repeated localStorage reads on every render
    this._decimals = Number(auth.getBillingDecimalPoint() || 2);
    // Columns are stable — build once so DataTable never re-renders on modal keystrokes
    this._columns = this._buildColumns();
  }

  componentDidMount() {
    this.fetchClients();
  }

  fetchClients = async () => {
    this.setState({ tableLoader: true, errorMessage: "" });
    try {
      const response = await listClientsWithCredits(
        auth.getAccount(),
        auth.getToken(),
        1,
        10000,
        "",
      );
      const payload = response && response.data;
      if (
        payload &&
        (payload.status === 403 ||
          payload.errors === "authentication missing" ||
          payload.errors === "jwt expired")
      ) {
        auth.logout();
        return;
      }
      if (payload && payload.status === 200 && payload.data) {
        const rows = payload.data.rows || [];
        const clientSelectOptions = rows.map((r) => ({
          value: r.accountno,
          label: [
            r.name || r.accountno,
            r.comp_name ? ` — ${r.comp_name}` : "",
            r.email ? ` · ${r.email}` : "",
          ].join(""),
        }));
        this.setState({
          allClients: rows,
          clientSelectOptions,
          tableData: rows.map((r) => this.toRow(r)),
          totals: payload.data.totals || {
            total_balance: 0,
            total_loaded: 0,
            total_applied: 0,
            clients_with_credit: 0,
          },
          tableLoader: false,
        });
      } else {
        this.setState({
          tableLoader: false,
          errorMessage:
            (payload && payload.message) ||
            "Failed to load clients with credits.",
        });
      }
    } catch (e) {
      this.setState({
        tableLoader: false,
        errorMessage: "Failed to load clients with credits.",
      });
    }
  };

  toRow = (r) => {
    return [
      r.accountno || "",
      r.name || "",
      r.comp_name || "",
      r.email || "",
      Number(r.balance) || 0,
      Number(r.tx_count) || 0,
      r.last_activity_at || "",
      Number(r.last_activity_amount) || 0,
      r.last_activity_type || "",
    ];
  };

  formatAmount = (value) => {
    const n = Number(value);
    if (!Number.isFinite(n)) return Number(0).toFixed(this._decimals);
    return n.toFixed(this._decimals);
  };

  formatActivity = (value) => {
    if (!value) return "—";
    try {
      const d = new Date(value);
      if (isNaN(d.getTime())) return value;
      const mm = String(d.getMonth() + 1).padStart(2, "0");
      const dd = String(d.getDate()).padStart(2, "0");
      const yyyy = d.getFullYear();
      return `${mm}-${dd}-${yyyy}`;
    } catch (e) {
      return value;
    }
  };

  // ---------- Add Credit modal ----------

  openAddCreditModal = (accountno) => {
    const addClientOption = accountno
      ? this.state.clientSelectOptions.find((o) => o.value === accountno) || null
      : null;
    this.setState({
      showAddModal: true,
      addClientAccountno: accountno || "",
      addClientOption,
      addAmount: "",
      addDate: new Date().toISOString().split("T")[0],
      addPaymentMethod: "Bank ACH",
      addReference: "",
      addMemo: "",
      addError: "",
      addSuccess: "",
    });
  };

  closeAddCreditModal = () => {
    if (this.state.addSubmitting) return;
    this.setState({
      showAddModal: false,
      addError: "",
      addSuccess: "",
      addReference: "",
      addMemo: "",
      addClientOption: null,
    });
  };

  submitAddCredit = async () => {
    const {
      addClientAccountno,
      addAmount,
      addDate,
      addPaymentMethod,
      addReference,
      addMemo,
    } = this.state;
    if (!addClientAccountno) {
      this.setState({ addError: "Please select a client." });
      return;
    }
    const amountNum = Number(addAmount);
    if (
      !addAmount ||
      !Number.isFinite(amountNum) ||
      amountNum <= 0 ||
      amountNum > 1000000
    ) {
      this.setState({
        addError: "Amount must be a positive number up to 1,000,000.",
      });
      return;
    }
    // Build description from reference + method + memo
    const parts = [];
    if (addReference) parts.push(addReference);
    if (addPaymentMethod) parts.push(`via ${addPaymentMethod}`);
    if (addDate) parts.push(`Date: ${addDate}`);
    if (addMemo) parts.push(`[Memo: ${addMemo}]`);
    const composedDescription = parts.length
      ? parts.join(" | ")
      : "Preloaded credit by admin";

    this.setState({ addSubmitting: true, addError: "", addSuccess: "" });
    try {
      const response = await addClientCredit(
        auth.getAccount(),
        auth.getToken(),
        addClientAccountno,
        amountNum,
        composedDescription,
      );
      const payload = response && response.data;
      if (
        payload &&
        (payload.status === 403 ||
          payload.errors === "authentication missing" ||
          payload.errors === "jwt expired")
      ) {
        auth.logout();
        return;
      }
      if (payload && payload.status === 200) {
        this.setState(
          { addSubmitting: false, addAmount: "", addReference: "", addMemo: "", showAddModal: false, toastMessage: "Credit added successfully.", toastType: "success", toastTitle: "Success" },
          () => { this.fetchClients(); },
        );
      } else {
        this.setState({
          addSubmitting: false,
          addError: (payload && payload.message) || "Failed to add credit.",
          toastMessage: (payload && payload.message) || "Failed to add credit.",
          toastType: "error",
          toastTitle: "Error",
        });
      }
    } catch (e) {
      this.setState({ addSubmitting: false, addError: "Failed to add credit.", toastMessage: "Failed to add credit.", toastType: "error", toastTitle: "Error" });
    }
  };

  // ---------- Deduct Credit modal ----------

  openDeductCreditModal = (accountno) => {
    const deductClientOption = accountno
      ? this.state.clientSelectOptions.find((o) => o.value === accountno) || null
      : null;
    this.setState({
      showDeductModal: true,
      deductClientAccountno: accountno || "",
      deductClientOption,
      deductAmount: "",
      deductDate: new Date().toISOString().split("T")[0],
      deductReference: "",
      deductMemo: "",
      deductError: "",
      deductSuccess: "",
    });
  };

  closeDeductCreditModal = () => {
    if (this.state.deductSubmitting) return;
    this.setState({
      showDeductModal: false,
      deductError: "",
      deductSuccess: "",
      deductReference: "",
      deductMemo: "",
      deductClientOption: null,
    });
  };

  submitDeductCredit = async () => {
    const {
      deductClientAccountno,
      deductAmount,
      deductDate,
      deductReference,
      deductMemo,
    } = this.state;
    if (!deductClientAccountno) {
      this.setState({ deductError: "Please select a client." });
      return;
    }
    const amountNum = Number(deductAmount);
    if (
      !deductAmount ||
      !Number.isFinite(amountNum) ||
      amountNum <= 0 ||
      amountNum > 1000000
    ) {
      this.setState({
        deductError: "Amount must be a positive number up to 1,000,000.",
      });
      return;
    }
    // Build description from reference + memo
    const parts = [];
    if (deductReference) parts.push(deductReference);
    if (deductDate) parts.push(`Date: ${deductDate}`);
    if (deductMemo) parts.push(`[Memo: ${deductMemo}]`);
    const composedDescription = parts.length
      ? parts.join(" | ")
      : "Credit deducted by admin";

    this.setState({ deductSubmitting: true, deductError: "", deductSuccess: "" });
    try {
      const response = await deductClientCredit(
        auth.getAccount(),
        auth.getToken(),
        deductClientAccountno,
        amountNum,
        composedDescription,
      );
      const payload = response && response.data;
      if (
        payload &&
        (payload.status === 403 ||
          payload.errors === "authentication missing" ||
          payload.errors === "jwt expired")
      ) {
        auth.logout();
        return;
      }
      if (payload && payload.status === 200) {
        this.setState(
          { deductSubmitting: false, deductAmount: "", deductReference: "", deductMemo: "", showDeductModal: false, toastMessage: "Credit deducted successfully.", toastType: "success", toastTitle: "Success" },
          () => { this.fetchClients(); },
        );
      } else {
        this.setState({
          deductSubmitting: false,
          deductError: (payload && payload.message) || "Failed to deduct credit.",
          toastMessage: (payload && payload.message) || "Failed to deduct credit.",
          toastType: "error",
          toastTitle: "Error",
        });
      }
    } catch (e) {
      this.setState({ deductSubmitting: false, deductError: "Failed to deduct credit.", toastMessage: "Failed to deduct credit.", toastType: "error", toastTitle: "Error" });
    }
  };

  // ---------- Ledger modal ----------

  openLedgerModal = (accountno) => {
    const client =
      this.state.allClients.find((c) => c.accountno === accountno) || null;
    this.setState(
      {
        showLedgerModal: true,
        ledgerClient: client,
        ledgerRows: [],
        ledgerPage: 1,
        ledgerLimit: 1000,
        ledgerTotal: 0,
        ledgerError: "",
      },
      this.loadLedger,
    );
  };

  closeLedgerModal = () => {
    this.setState({ showLedgerModal: false, ledgerClient: null });
  };

  loadLedger = async () => {
    const { ledgerClient, ledgerPage, ledgerLimit } = this.state;
    if (!ledgerClient) return;
    this.setState({ ledgerLoading: true, ledgerError: "" });
    try {
      const response = await getClientCreditLedger(
        auth.getAccount(),
        auth.getToken(),
        ledgerClient.accountno,
        ledgerPage,
        ledgerLimit,
      );
      const payload = response && response.data;
      if (
        payload &&
        (payload.status === 403 ||
          payload.errors === "authentication missing" ||
          payload.errors === "jwt expired")
      ) {
        auth.logout();
        return;
      }
      if (payload && payload.status === 200 && payload.data) {
        this.setState({
          ledgerRows: payload.data.rows || [],
          ledgerTotal: Number(payload.data.total) || 0,
          ledgerLoading: false,
        });
      } else {
        this.setState({
          ledgerLoading: false,
          ledgerError:
            (payload && payload.message) || "Failed to load ledger.",
        });
      }
    } catch (e) {
      this.setState({
        ledgerLoading: false,
        ledgerError: "Failed to load ledger.",
      });
    }
  };

  onLedgerPageChange = (page) => {
    this.setState({ ledgerPage: page }, this.loadLedger);
  };

  onLedgerLimitChange = (limit) => {
    this.setState({ ledgerLimit: limit, ledgerPage: 1 }, this.loadLedger);
  };

  formatLedgerType = (type) => {
    if (type === "credit_added") return "Credit Added";
    if (type === "invoice_payment") return "Invoice Payment";
    if (type === "manual_adjustment") return "Manual Adjustment";
    if (type === "debit") return "Debit";
    return type || "—";
  };

  formatLedgerDate = (value) => {
    if (!value) return "—";
    try {
      const d = new Date(value);
      if (isNaN(d.getTime())) return value;
      const mm = String(d.getMonth() + 1).padStart(2, "0");
      const dd = String(d.getDate()).padStart(2, "0");
      const yyyy = d.getFullYear();
      return `${mm}-${dd}-${yyyy}`;
    } catch (e) {
      return value;
    }
  };

  // ---------- KPI cards ----------

  renderKpiCards = () => {
    const t = this.state.totals || {};
    const cards = [
      {
        label: "Available Balance",
        value: this.formatAmount(t.total_balance),
        prefix: "$",
        color: "#1c5bde",
        bg: "#e8eefb",
        icon: "ni ni-wallet",
      },
      {
        label: "Clients with Credit",
        value: String(Number(t.clients_with_credit) || 0),
        suffix: " clients",
        color: "#1a7a4a",
        bg: "#e6f4ec",
        icon: "ni ni-users",
      },
      {
        label: "Total Loaded",
        value: this.formatAmount(t.total_loaded),
        prefix: "+$",
        color: "#312f2d",
        bg: "#fafaf9",
        icon: "ni ni-arrow-down",
      },
      {
        label: "Total Applied",
        value: this.formatAmount(t.total_applied),
        prefix: "−$",
        color: "#d97706",
        bg: "#fef3c7",
        icon: "ni ni-arrow-up",
      },
    ];
    return (
      <div style={{ display: "flex", gap: 12, flexWrap: "wrap", marginBottom: 24 }}>
        {cards.map((c, idx) => (
          <div key={idx} style={{ display: "contents" }}>
            <div className="card card-bordered" style={{ flex: "1 1 190px", minWidth: 180, minHeight: 84, borderRadius: 8 }}>
              <div className="card-inner" style={{ minHeight: 82, padding: "14px 16px", position: "relative" }}>
                <div style={{ display: "block", marginBottom: 4, paddingLeft: 48 }}>
                  <div style={{ fontSize: 10, fontWeight: 800, textTransform: "uppercase", color: "#6f6a64" }}>{c.label}</div>
                  <div style={{ width: 36, height: 36, borderRadius: 8, background: c.bg, display: "flex", alignItems: "center", justifyContent: "center", color: c.color, position: "absolute", left: 16, top: "50%", transform: "translateY(-50%)" }}>
                    <em className={"icon " + c.icon} style={{ fontSize: 18 }}></em>
                  </div>
                </div>
                <div style={{ fontSize: 20, fontWeight: 800, color: "#111827", lineHeight: 1.05, marginLeft: 48 }}>
                  {c.prefix || ""}{c.value}{c.suffix || ""}
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>
    );
  };

  // ---------- DataTable columns ----------

  _buildColumns = () => {
    return [
      {
        name: "Id",
        options: { display: false, filter: false, viewColumns: false },
      },
      {
        name: "Client",
        options: {
          customBodyRender: (value) => (
            <div style={{ fontWeight: 700 }}>{value || "—"}</div>
          ),
        },
      },
      {
        name: "Company",
        options: {
          customBodyRender: (value) => <span>{value || "—"}</span>,
        },
      },
      {
        name: "Email",
        options: {
          customBodyRender: (value) => <span>{value || "—"}</span>,
        },
      },
      {
        name: "Credit Balance",
        options: {
          customBodyRender: (value) => (
            <span
              style={{
                fontWeight: 700,
                color: Number(value) > 0 ? "#1c5bde" : "#b8b4ad",
              }}
            >
              ${this.formatAmount(value)}
            </span>
          ),
        },
      },
      {
        name: "Transactions",
        options: {
          customBodyRender: (value) =>
            Number(value) > 0 ? (
              <span className="badge badge-outline-primary">
                {value} tx
              </span>
            ) : (
              <span className="text-soft">—</span>
            ),
        },
      },
      {
        name: "Last Activity",
        options: {
          customBodyRender: (value, tableMeta) => {
            const amount = tableMeta.rowData[7];
            const type = tableMeta.rowData[8];
            const isCredit = type !== "credit_used" && type !== "debit";
            return (
              <div>
                {amount !== 0 ? (
                  <div style={{ fontWeight: 700, color: isCredit ? "#1a7a4a" : "#e85347", fontSize: "13px" }}>
                    {isCredit ? "+" : "−"}${this.formatAmount(Math.abs(amount))}
                  </div>
                ) : null}
                <div className="text-soft" style={{ fontSize: "12px" }}>
                  {this.formatActivity(value)}
                </div>
              </div>
            );
          },
        },
      },
      {
        name: "_AmtHidden",
        options: { display: false, filter: false, viewColumns: false },
      },
      {
        name: "_TypeHidden",
        options: { display: false, filter: false, viewColumns: false },
      },
      {
        name: "Actions",
        options: {
          filter: false,
          sort: false,
          stickyRight: true,
          customHeadLabelRender: () => "Actions",
          customBodyRender: (value, tableMeta) => {
            const accountno = tableMeta.rowData[0];
            return (
              <ul className="nk-tb-actions gx-1">
                <li>
                  <Tooltip title="Load Credit" placement="top">
                    <a
                      href="#add-credit"
                      onClick={(e) => { e.preventDefault(); this.openAddCreditModal(accountno); }}
                      className="btn btn-trigger btn-icon"
                    >
                      <em className="icon ni ni-wallet-in" style={{ color: "#046294" }}></em>
                    </a>
                  </Tooltip>
                </li>
                {/* <li>
                  <a
                    href="#deduct-credit"
                    onClick={(e) => {
                      e.preventDefault();
                      this.openDeductCreditModal(accountno);
                    }}
                    className="btn btn-trigger btn-icon"
                    title="Deduct credit"
                  >
                    <em
                      className="icon ni ni-minus"
                      style={{ color: "rgb(232, 83, 71)" }}
                    ></em>
                  </a>
                </li> */}
                <li>
                  <Tooltip title="View Ledger" placement="top">
                    <a
                      href="#view-ledger"
                      onClick={(e) => { e.preventDefault(); this.openLedgerModal(accountno); }}
                      className="btn btn-trigger btn-icon"
                    >
                      <em className="icon ni ni-file-docs" style={{ color: "#e6a817" }}></em>
                    </a>
                  </Tooltip>
                </li>
              </ul>
            );
          },
        },
      },
    ];
  };

  // ---------- Modals ----------

  renderAddCreditModal = () => {
    if (!this.state.showAddModal) return null;
    return (
      <div
        className="modal fade show"
        style={{ display: "block", background: "rgba(0,0,0,0.45)" }}
        tabIndex="-1"
        role="dialog"
        onClick={(e) => { if (e.target === e.currentTarget) this.closeAddCreditModal(); }}
      >
        <div className="modal-dialog modal-dialog-centered" role="document">
          <div className="modal-content">
            <a
              href="#cancel"
              className="close"
              onClick={(e) => {
                e.preventDefault();
                this.closeAddCreditModal();
              }}
            >
              <em className="icon ni ni-cross-sm"></em>
            </a>
            <div className="modal-body modal-body-md">
              <h5 className="title">Load Client Credit</h5>
              <div
                className="text-soft"
                style={{ fontSize: "13px", marginBottom: "14px" }}
              >
                Add a credit balance for a client.
              </div>

              {this.state.addError ? (
                <div className="alert alert-pro alert-danger mb-3">
                  <div className="alert-text">
                    <p>{this.state.addError}</p>
                  </div>
                </div>
              ) : null}
              {this.state.addSuccess ? (
                <div className="alert alert-pro alert-success mb-3">
                  <div className="alert-text">
                    <p>{this.state.addSuccess}</p>
                  </div>
                </div>
              ) : null}

              <div className="form-group">
                <Autocomplete
                  options={this.state.clientSelectOptions}
                  getOptionLabel={(opt) => opt.label || ""}
                  value={this.state.addClientOption}
                  onChange={(_, opt) => this.setState({ addClientAccountno: opt ? opt.value : "", addClientOption: opt || null })}
                  disabled={this.state.addSubmitting}
                  renderInput={(params) => (
                    <MutextField
                      {...params}
                      required
                      fullWidth
                      variant="outlined"
                      label="Client"
                      placeholder="Search client by name, company or email…"
                      InputLabelProps={{ ...params.InputLabelProps, style: { color: undefined } }}
                    />
                  )}
                />
              </div>

              <div className="row" style={{ marginBottom: 16 }}>
                <div className="col-sm-6">
                  <MutextField
                    required fullWidth variant="outlined"
                    label="Credit Amount (USD)"
                    type="number"
                    inputProps={{ step: "0.01", min: "0.01", max: "1000000" }}
                    placeholder="0.00"
                    value={this.state.addAmount}
                    onChange={(e) => this.setState({ addAmount: e.target.value })}
                    disabled={this.state.addSubmitting}
                  />
                </div>
                <div className="col-sm-6">
                  <MutextField
                    required fullWidth variant="outlined"
                    label="Date"
                    type="date"
                    InputLabelProps={{ shrink: true }}
                    value={this.state.addDate}
                    onChange={(e) => this.setState({ addDate: e.target.value })}
                    disabled={this.state.addSubmitting}
                  />
                </div>
              </div>

              <div className="form-group" style={{ marginBottom: 16 }}>
                <Autocomplete
                  options={["Bank ACH", "Wire Transfer", "Credit Card", "Check", "Other"]}
                  value={this.state.addPaymentMethod}
                  onChange={(_, val) => this.setState({ addPaymentMethod: val || "Bank ACH" })}
                  disabled={this.state.addSubmitting}
                  disableClearable
                  renderInput={(params) => (
                    <MutextField {...params} fullWidth variant="outlined" label="Payment Method" />
                  )}
                />
              </div>

              <div className="form-group">
                <MutextField
                  fullWidth variant="outlined"
                  label="Reference / Note"
                  placeholder="e.g. Wire transfer ref #1234, Check #5678…"
                  inputProps={{ maxLength: 500 }}
                  value={this.state.addReference}
                  onChange={(e) => this.setState({ addReference: e.target.value })}
                  disabled={this.state.addSubmitting}
                />
              </div>

              <div className="form-group">
                <MutextField
                  fullWidth multiline rows={3} variant="outlined"
                  label="Internal Memo (optional)"
                  placeholder="Internal notes about this credit load…"
                  inputProps={{ maxLength: 1000 }}
                  value={this.state.addMemo}
                  onChange={(e) => this.setState({ addMemo: e.target.value })}
                  disabled={this.state.addSubmitting}
                />
              </div>

              <div className="form-group">
                <div style={{ display: "flex", justifyContent: "flex-end", alignItems: "center", gap: "16px" }}>
                  <a
                    href="#cancel"
                    onClick={(e) => {
                      e.preventDefault();
                      this.closeAddCreditModal();
                    }}
                    className="btn btn-outline-light"
                  >
                    Cancel
                  </a>
                  <button
                    type="button"
                    className="btn btn-primary"
                    onClick={this.submitAddCredit}
                    disabled={this.state.addSubmitting}
                  >
                    {this.state.addSubmitting ? (
                      <span>
                        <span
                          className="spinner-border spinner-border-sm"
                          role="status"
                          aria-hidden="true"
                        ></span>
                        <span> Saving…</span>
                      </span>
                    ) : (
                      <span>Load Credit</span>
                    )}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  };

  renderDeductCreditModal = () => {
    if (!this.state.showDeductModal) return null;
    return (
      <div
        className="modal fade show"
        style={{ display: "block", background: "rgba(0,0,0,0.45)" }}
        tabIndex="-1"
        role="dialog"
        onClick={(e) => { if (e.target === e.currentTarget) this.closeDeductCreditModal(); }}
      >
        <div className="modal-dialog modal-dialog-centered" role="document">
          <div className="modal-content">
            <a
              href="#cancel"
              className="close"
              onClick={(e) => {
                e.preventDefault();
                this.closeDeductCreditModal();
              }}
            >
              <em className="icon ni ni-cross-sm"></em>
            </a>
            <div className="modal-body modal-body-md">
              <h5 className="title">Deduct Client Credit</h5>
              <div
                className="text-soft"
                style={{ fontSize: "13px", marginBottom: "14px" }}
              >
                Deduct credit balance from a client.
              </div>

              {this.state.deductError ? (
                <div className="alert alert-pro alert-danger mb-3">
                  <div className="alert-text">
                    <p>{this.state.deductError}</p>
                  </div>
                </div>
              ) : null}
              {this.state.deductSuccess ? (
                <div className="alert alert-pro alert-success mb-3">
                  <div className="alert-text">
                    <p>{this.state.deductSuccess}</p>
                  </div>
                </div>
              ) : null}

              <div className="form-group">
                <Autocomplete
                  options={this.state.clientSelectOptions}
                  getOptionLabel={(opt) => opt.label || ""}
                  value={this.state.deductClientOption}
                  onChange={(_, opt) => this.setState({ deductClientAccountno: opt ? opt.value : "", deductClientOption: opt || null })}
                  disabled={this.state.deductSubmitting}
                  renderInput={(params) => (
                    <MutextField
                      {...params}
                      required
                      fullWidth
                      variant="outlined"
                      label="Client"
                      placeholder="Search client by name, company or email…"
                      InputLabelProps={{ ...params.InputLabelProps, style: { color: undefined } }}
                    />
                  )}
                />
              </div>

              <div className="row" style={{ marginBottom: 16 }}>
                <div className="col-sm-6">
                  <MutextField
                    required fullWidth variant="outlined"
                    label="Deduct Amount (USD)"
                    type="number"
                    inputProps={{ step: "0.01", min: "0.01", max: "1000000" }}
                    placeholder="0.00"
                    value={this.state.deductAmount}
                    onChange={(e) => this.setState({ deductAmount: e.target.value })}
                    disabled={this.state.deductSubmitting}
                  />
                </div>
                <div className="col-sm-6">
                  <MutextField
                    required fullWidth variant="outlined"
                    label="Date"
                    type="date"
                    InputLabelProps={{ shrink: true }}
                    value={this.state.deductDate}
                    onChange={(e) => this.setState({ deductDate: e.target.value })}
                    disabled={this.state.deductSubmitting}
                  />
                </div>
              </div>

              <div className="form-group" style={{ marginBottom: 16 }}>
                <Autocomplete
                  options={["Bank ACH", "Wire Transfer", "Credit Card", "Check", "Other"]}
                  value={this.state.deductPaymentMethod || "Bank ACH"}
                  onChange={(_, val) => this.setState({ deductPaymentMethod: val || "Bank ACH" })}
                  disabled={this.state.deductSubmitting}
                  disableClearable
                  renderInput={(params) => (
                    <MutextField {...params} fullWidth variant="outlined" label="Payment Method" />
                  )}
                />
              </div>

              <div className="form-group">
                <MutextField
                  fullWidth variant="outlined"
                  label="Reference / Note"
                  placeholder="e.g. Adjustment for refund, correction…"
                  inputProps={{ maxLength: 500 }}
                  value={this.state.deductReference}
                  onChange={(e) => this.setState({ deductReference: e.target.value })}
                  disabled={this.state.deductSubmitting}
                />
              </div>

              <div className="form-group">
                <MutextField
                  fullWidth multiline rows={3} variant="outlined"
                  label="Internal Memo (optional)"
                  placeholder="Internal notes about this deduction…"
                  inputProps={{ maxLength: 1000 }}
                  value={this.state.deductMemo}
                  onChange={(e) => this.setState({ deductMemo: e.target.value })}
                  disabled={this.state.deductSubmitting}
                />
              </div>

              <div className="form-group">
                <div style={{ display: "flex", justifyContent: "flex-end", alignItems: "center", gap: "16px" }}>
                  <a
                    href="#cancel"
                    onClick={(e) => {
                      e.preventDefault();
                      this.closeDeductCreditModal();
                    }}
                    className="btn btn-outline-light"
                  >
                    Cancel
                  </a>
                  <button
                    type="button"
                    className="btn btn-primary"
                    onClick={this.submitDeductCredit}
                    disabled={this.state.deductSubmitting}
                  >
                    {this.state.deductSubmitting ? (
                      <span>
                        <span
                          className="spinner-border spinner-border-sm"
                          role="status"
                          aria-hidden="true"
                        ></span>
                        <span> Saving…</span>
                      </span>
                    ) : (
                      <span>Add Debit</span>
                    )}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  };

  renderLedgerModal = () => {
    if (!this.state.showLedgerModal) return null;
    const client = this.state.ledgerClient || {};
    const { ledgerRows, ledgerLoading, ledgerError } = this.state;

    // Build chronological rows (API returns newest-first, reverse for display)
    const chronoRows = [...ledgerRows].reverse();

    // Compute summary stats
    let totalCredits = 0;
    let totalDebits = 0;
    chronoRows.forEach((r) => {
      const amt = Number(r.amount) || 0;
      if (amt > 0) totalCredits += amt;
      else totalDebits += Math.abs(amt);
    });
    const currentBalance = Number(client.balance) || 0;

    // Build rows with running balance
    let running = 0;
    const displayRows = chronoRows.map((r) => {
      running += Number(r.amount) || 0;
      return { ...r, runningBalance: running };
    });
    const openingBalance = currentBalance - running;

    const tdStyle = {
      padding: "9px 14px",
      fontSize: "13px",
      borderBottom: "1px solid #f5f5f5",
    };
    const thStyle = {
      padding: "8px 14px",
      fontSize: "10.5px",
      fontWeight: 700,
      textTransform: "uppercase",
      letterSpacing: "0.05em",
      color: "#9a9690",
      background: "#fafaf9",
      borderBottom: "1px solid #e5e5e5",
    };

    return (
      <div
        className="modal fade show"
        style={{ display: "block", background: "rgba(0,0,0,0.45)" }}
        tabIndex="-1"
        role="dialog"
        onClick={(e) => { if (e.target === e.currentTarget) this.closeLedgerModal(); }}
      >
        <div
          className="modal-dialog modal-dialog-centered modal-xl"
          role="document"
        >
          <div className="modal-content">
            <a
              href="#cancel"
              className="close"
              onClick={(e) => {
                e.preventDefault();
                this.closeLedgerModal();
              }}
            >
              <em className="icon ni ni-cross-sm"></em>
            </a>
            <div className="modal-body" style={{ padding: 0 }}>
              {/* Header */}
              <div style={{ padding: "20px 24px 12px" }}>
                <h5 className="title" style={{ marginBottom: "2px" }}>
                  Credit Ledger
                  {client.name ? ` — ${client.name}` : ""}
                </h5>
                <div className="text-soft" style={{ fontSize: "13px" }}>
                  {client.comp_name ? `${client.comp_name} · ` : ""}
                  Current Balance:{" "}
                  <strong style={{ color: currentBalance > 0 ? "#1c5bde" : "#9a9690" }}>
                    ${this.formatAmount(currentBalance)}
                  </strong>
                </div>
              </div>

              {ledgerError ? (
                <div className="alert alert-pro alert-danger" style={{ margin: "0 24px 12px" }}>
                  <div className="alert-text"><p>{ledgerError}</p></div>
                </div>
              ) : null}

              {/* Summary strip */}
              <div style={{ padding: "0 24px 12px" }}>
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "1fr 1fr 1fr 1fr",
                  gap: "1px",
                  background: "#e5e5e5",
                  border: "1px solid #e5e5e5",
                  borderRadius: 8,
                  overflow: "hidden",
                }}
              >
                {[
                  ["Total Credits", `+$${this.formatAmount(totalCredits)}`, "#1a7a4a"],
                  ["Total Debits", `−$${this.formatAmount(totalDebits)}`, "#e85347"],
                  ["Transactions", String(chronoRows.length), "#1c5bde"],
                  [
                    "Current Balance",
                    `$${this.formatAmount(currentBalance)}`,
                    currentBalance > 0 ? "#1c5bde" : "#9a9690",
                  ],
                ].map(([lbl, val, col], i) => (
                  <div
                    key={i}
                    style={{
                      padding: "12px 16px",
                      background: "#fff",
                    }}
                  >
                    <div
                      style={{
                        fontSize: "10.5px",
                        fontWeight: 700,
                        textTransform: "uppercase",
                        letterSpacing: "0.05em",
                        color: "#9a9690",
                        marginBottom: "3px",
                      }}
                    >
                      {lbl}
                    </div>
                    <div
                      style={{
                        fontSize: "18px",
                        fontWeight: 800,
                        color: col,
                        letterSpacing: "-0.01em",
                      }}
                    >
                      {val}
                    </div>
                  </div>
                ))}
              </div>
              </div>

              {/* Ledger table */}
              <div style={{ overflowX: "auto", padding: "0 24px", paddingBottom: 4 }}>
                {ledgerLoading ? (
                  <div style={{ padding: "32px", textAlign: "center", color: "#9a9690" }}>
                    <span
                      className="spinner-border spinner-border-sm"
                      role="status"
                    />
                    <span style={{ marginLeft: "8px" }}>Loading…</span>
                  </div>
                ) : displayRows.length === 0 ? (
                  <div style={{ padding: "40px", textAlign: "center", color: "#9a9690" }}>
                    <em
                      className="icon ni ni-coin-alt"
                      style={{ fontSize: "32px", display: "block", marginBottom: "10px" }}
                    />
                    <div style={{ fontWeight: 600, color: "#526484" }}>No transactions yet</div>
                    <div style={{ fontSize: "13px", marginTop: "4px" }}>
                      Load credit to start the ledger.
                    </div>
                  </div>
                ) : (
                  <table
                    style={{
                      width: "100%",
                      borderCollapse: "collapse",
                      minWidth: "600px",
                    }}
                  >
                    <thead>
                      <tr>
                        <th style={thStyle}>Date</th>
                        <th style={thStyle}>Description</th>
                        <th style={{ ...thStyle, textAlign: "center" }}>Type</th>
                        <th style={{ ...thStyle, textAlign: "right", color: "#1a7a4a" }}>Credit (+)</th>
                        <th style={{ ...thStyle, textAlign: "right", color: "#e85347" }}>Debit (−)</th>
                        <th style={{ ...thStyle, textAlign: "right" }}>Balance</th>
                      </tr>
                    </thead>
                    <tbody>
                      {/* Opening balance row */}
                      <tr style={{ background: "#fafaf9" }}>
                        <td
                          colSpan={5}
                          style={{ ...tdStyle, color: "#9a9690", fontStyle: "italic" }}
                        >
                          Opening Balance
                        </td>
                        <td
                          style={{
                            ...tdStyle,
                            textAlign: "right",
                            fontWeight: 700,
                            color: "#526484",
                          }}
                        >
                          ${this.formatAmount(openingBalance)}
                        </td>
                      </tr>

                      {/* Transaction rows */}
                      {displayRows.map((row, idx) => {
                        const amt = Number(row.amount) || 0;
                        const isCredit = amt >= 0;
                        return (
                          <tr
                            key={row.id || idx}
                            style={{
                              background: idx % 2 === 0 ? "#fff" : "#fafaf9",
                            }}
                          >
                            <td style={{ ...tdStyle, color: "#526484", whiteSpace: "nowrap" }}>
                              {this.formatLedgerDate(row.created_at)}
                            </td>
                            <td
                              style={{
                                ...tdStyle,
                                wordBreak: "break-word",
                                whiteSpace: "normal",
                              }}
                            >
                              {(() => {
                                const dp = (row.description || "").split(" | ").filter(p => !p.startsWith("Date:") && !p.startsWith("[Memo:"));
                                return dp.join(" ") || "—";
                              })()}
                            </td>
                            <td style={{ ...tdStyle, textAlign: "center" }}>
                              <span
                                className={`badge badge-pill badge-outline-${isCredit ? "success" : "danger"}`}
                                style={{ fontSize: "11px", fontWeight: 700 }}
                              >
                                {isCredit ? "CREDIT" : "DEBIT"}
                              </span>
                            </td>
                            <td
                              style={{
                                ...tdStyle,
                                textAlign: "right",
                                fontWeight: 700,
                                color: isCredit ? "#1a7a4a" : "#9a9690",
                              }}
                            >
                              {isCredit ? `$${this.formatAmount(amt)}` : "—"}
                            </td>
                            <td
                              style={{
                                ...tdStyle,
                                textAlign: "right",
                                fontWeight: 700,
                                color: !isCredit ? "#e85347" : "#9a9690",
                              }}
                            >
                              {!isCredit ? `$${this.formatAmount(Math.abs(amt))}` : "—"}
                            </td>
                            <td
                              style={{
                                ...tdStyle,
                                textAlign: "right",
                                fontWeight: 800,
                                color:
                                  row.runningBalance > 0 ? "#1c5bde" : "#9a9690",
                              }}
                            >
                              ${this.formatAmount(row.runningBalance)}
                            </td>
                          </tr>
                        );
                      })}

                      {/* Closing balance row */}
                      <tr style={{ background: "#f0f6fa", borderTop: "2px solid #046294" }}>
                        <td
                          colSpan={5}
                          style={{
                            ...tdStyle,
                            fontWeight: 800,
                            color: "#046294",
                            fontSize: "13px",
                            textTransform: "uppercase",
                            letterSpacing: "0.05em",
                            borderBottom: "none",
                          }}
                        >
                          Closing Balance
                        </td>
                        <td
                          style={{
                            ...tdStyle,
                            textAlign: "right",
                            fontWeight: 900,
                            fontSize: "13px",
                            color: "#046294",
                            borderBottom: "none",
                          }}
                        >
                          ${this.formatAmount(currentBalance)}
                        </td>
                      </tr>
                    </tbody>
                  </table>
                )}
              </div>

              {/* Footer */}
              <div
                style={{
                  padding: "14px 24px",
                  borderTop: "1px solid #e5e5e5",
                  display: "flex",
                  gap: "10px",
                  alignItems: "center",
                  justifyContent: "flex-end",
                }}
              >
                <a
                  href="#close"
                  onClick={(e) => {
                    e.preventDefault();
                    this.closeLedgerModal();
                  }}
                  className="btn btn-outline-light"
                >
                  Close
                </a>
                <button
                  type="button"
                  className="btn btn-dim btn-secondary"
                  onClick={() => {
                    // CSV export
                    const cols = ["Date", "Description", "Type", "Credit", "Debit", "Balance"];
                    const escCsv = (v) => `"${String(v == null ? "" : v).replace(/"/g, '""')}"`;
                    const rowsCsv = displayRows.map((r) => {
                      const amt = Number(r.amount) || 0;
                      const isCredit = amt >= 0;
                      return [
                        this.formatLedgerDate(r.created_at),
                        r.description || "",
                        isCredit ? "CREDIT" : "DEBIT",
                        isCredit ? this.formatAmount(amt) : "",
                        !isCredit ? this.formatAmount(Math.abs(amt)) : "",
                        this.formatAmount(r.runningBalance),
                      ].map(escCsv).join(",");
                    });
                    const csv = [cols.map(escCsv).join(","), ...rowsCsv].join("\n");
                    const blob = new Blob([csv], { type: "text/csv" });
                    const url = URL.createObjectURL(blob);
                    const a = document.createElement("a");
                    a.href = url;
                    a.download = `ledger-${client.accountno || "export"}.csv`;
                    a.click();
                    URL.revokeObjectURL(url);
                  }}
                >
                  <em className="icon ni ni-download"></em>
                  <span>Export CSV</span>
                </button>
                <button
                  type="button"
                  className="btn btn-primary"
                  onClick={() => {
                    this.closeLedgerModal();
                    this.openAddCreditModal(client.accountno);
                  }}
                >
                  <em className="icon ni ni-plus"></em>
                  <span>Add Credit</span>
                </button>
                <button
                  type="button"
                  className="btn btn-primary"
                  onClick={() => {
                    this.closeLedgerModal();
                    this.openDeductCreditModal(client.accountno);
                  }}
                >
                  <em className="icon ni ni-minus"></em>
                  <span>Add Debit</span>
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  };

  render() {
    return (
      <div className="nk-content" id="LoadCredit_Block">
        <div className="container-fluid">
          {this.props.defaultSettings === "missing" ? <SettingsError /> : null}
          {this.props.is_msa_signed === "No" ? <MsaWarning /> : null}

          <div className="nk-content-inner">
            <div className="nk-content-body">
              <div className="nk-block-head nk-block-head-sm">
                <div className="nk-block-between">
                  <div className="nk-block-head-content">
                    <h3 className="nk-block-title page-title">
                      <PageTitle
                        name="Client Credits"
                        icon="icon ni ni-coins"
                      />
                    </h3>
                    <div
                      className="text-soft"
                      style={{ fontSize: "13px", marginTop: "4px" }}
                    >
                      Preload credits for clients and view their credit
                      activity.
                    </div>
                  </div>
                  <div className="nk-block-head-content">
                    <div style={{ display: "flex", gap: "8px" }}>
                      <button
                        type="button"
                        className="btn btn-primary"
                        onClick={() => this.openAddCreditModal("")}
                      >
                        <em className="icon ni ni-plus"></em>
                        <span>Load Credit</span>
                      </button>
                      <button
                        type="button"
                        className="btn btn-primary"
                        onClick={() => this.openDeductCreditModal("")}
                      >
                        <em className="icon ni ni-minus"></em>
                        <span>Add Debit</span>
                      </button>
                    </div>
                  </div>
                </div>
              </div>

              {this.renderKpiCards()}

              <div className="nk-block mt-5">
                <div className="card card-bordered">
                  <div className="card-inner">
                    {this.state.errorMessage ? (
                      <div className="alert alert-pro alert-danger mb-3">
                        <div className="alert-text">
                          <p>{this.state.errorMessage}</p>
                        </div>
                      </div>
                    ) : null}

                    {this.state.tableLoader === true ? (
                      tableLoader()
                    ) : (
                      <DataTable
                        columns={this._columns}
                        tableData={this.state.tableData}
                        title=""
                        from="clients"
                      />
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
        {this.renderAddCreditModal()}
        {this.renderDeductCreditModal()}
        {this.renderLedgerModal()}
        <NotificationToast
          message={this.state.toastMessage}
          type={this.state.toastType}
          title={this.state.toastTitle}
          onDismiss={() => this.setState({ toastMessage: "" })}
        />
      </div>
    );
  }
}

const mapStateToProps = (state) => ({
  defaultSettings: state.settings,
  is_msa_signed: state.is_msa_signed,
});

const mapDispatchToProps = (dispatch) => ({
  addSettings: (payload) => dispatch(addSettings(payload)),
});

export default connect(
  mapStateToProps,
  mapDispatchToProps,
)(withRouter(LoadCredit));
